import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { KeyRound, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const WEAVE_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_essex-teams/artifacts/mur72jzi_WEAVE-LOGO%202024.webp";
const ESSEX_LOGO = "https://customer-assets.emergentagent.com/job_48a96b5b-7714-4454-98cb-57b574138d27/artifacts/xfc3bfwg_essex_business_school_logo.jpg";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: token.trim(), new_password: password });
      toast.success("Password updated — sign in with your new password");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDFDFD]">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between gap-4 mb-8">
          <img src={WEAVE_LOGO} alt="The Weave" className="h-16 w-auto object-contain" />
          <div className="font-display text-base sm:text-lg font-extrabold text-center leading-tight">
            The Weave<br />Workbook
          </div>
          <img src={ESSEX_LOGO} alt="Essex Business School" className="h-12 w-auto object-contain bg-white" />
        </div>
        <Link to="/login" className="inline-flex items-center text-xs uppercase tracking-wider text-[#525252] hover:text-[#0A0A0A] mb-6" data-testid="reset-back">
          <ChevronLeft size={14} /> Back to sign in
        </Link>

        <div className="label-eyebrow">Reset password</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2">Set a new password</h1>
        <p className="mt-3 text-sm text-[#525252]">
          Use the link your faculty shared. Once set, sign in with your email and new password.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
          <div>
            <Label className="label-eyebrow">Reset token</Label>
            <Input
              required
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="mt-2 rounded-none h-11 font-mono text-xs"
              placeholder="From the link your faculty shared"
              data-testid="reset-token-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow">New password</Label>
            <PasswordInput
              required minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 rounded-none h-11"
              data-testid="reset-password-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Confirm new password</Label>
            <PasswordInput
              required minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 rounded-none h-11"
              data-testid="reset-confirm-input"
            />
          </div>
          {error && (
            <div className="border border-[#e93e36] bg-[#FDEDED] px-3 py-2 text-sm text-[#7A1F1F]" data-testid="reset-error">
              {error}
            </div>
          )}
          <Button type="submit" disabled={busy} className="w-full h-11 rounded-none bg-[#2596be] hover:bg-[#1e7da3] uppercase tracking-wide text-xs font-semibold" data-testid="reset-submit-btn">
            <KeyRound size={14} className="mr-2" />
            {busy ? "Setting…" : "Set new password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
